import { ROUTES } from "../constants";
import { apiRequest } from "./api.client";
import { PresignedUrlEntry } from "./onboarding.service";

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// Each sub-section gets its own block. Two interfaces per form-based section:
//   - Data   → what the GET response looks like (what we render)
//   - Input  → what the PUT/POST body sends (what the user edits)
// ─────────────────────────────────────────────────────────────────────────────

// ── Shared ───────────────────────────────────────────────────────────────────

/** Full user object from GET /auth/me — richer than OnboardingAPI.getMe() */
export interface CurrentUser {
  user_id:          string;
  email:            string;
  first_name:       string;
  last_name:        string;
  role:             "Doctor" | "Receptionist" | "Admin";
  clinic_id:        string | null;
  phone:            string | null;   // from users.phone
  designation:      string | null;   // pending Alembic migration
  specializations:  string[];        // pending Alembic migration (TEXT[])
  experience_years: number | null;   // from Doctor table via user_id join
}

// ── Profile & Account ────────────────────────────────────────────────────────

/** Profile of a NON-doctor user (Admin / Receptionist): identity + clinic only.
 *  `role` is the discriminator used to decide doctor vs non-doctor. */
export interface UserProfile {
  user_id:        string;
  first_name:     string;
  last_name:      string;
  email:          string;
  phone:          string;
  designation:    string;
  role:           string;
  clinic_id:      string;
  clinic_name:    string;
  clinic_address: string;
  logo_s3_key:    string | null;
  photo_url:      string | null;   // short-lived presigned GET for the profile photo
}

/** One weekday's availability for the Professional Bio "Clinic Timings".
 *  Sourced from the doctor_availability_schedules table (edited via the Roster),
 *  not the clinic-wide free-text timings. Times are 24-hour "HH:MM". */
export interface DoctorAvailabilityItem {
  day_of_week: number;   // 0=Monday … 6=Sunday
  day_name:    string;   // "Monday" … "Sunday"
  start_time:  string;   // "HH:MM"
  end_time:    string;   // "HH:MM"
}

/** Profile of a DOCTOR (or an admin-who-practices): UserProfile + the
 *  doctor-sourced fields. Returned by the backend only when the caller can act
 *  as a doctor (Doctor, or Admin with "Doctor writes" — a linked doctor row +
 *  verified medical registration). doctor_id is the discriminator and gates the
 *  Doctor KYC card. */
export interface DoctorProfile extends UserProfile {
  doctor_id:           string;
  registration_number: string | null;
  timings_display:     string;
  availability:        DoctorAvailabilityItem[];
}

/** What getProfile() returns — a DoctorProfile for doctor-capable users, a plain
 *  UserProfile otherwise. */
export type ClinicProfile = UserProfile | DoctorProfile;

/** Narrow a profile to DoctorProfile. The backend attaches the doctor-only
 *  fields (keyed by doctor_id) exactly when the caller may manage doctor data,
 *  so the presence of doctor_id — not the role string — is the discriminator
 *  (an admin-who-practices has role "Admin" but still carries doctor data). */
export const isDoctorProfile = (p: ClinicProfile): p is DoctorProfile =>
  "doctor_id" in p && !!(p as DoctorProfile).doctor_id;

/** What the My Profile card saves */
export interface ClinicProfileInput {
  first_name:  string;
  last_name:   string;
  phone:       string;
  designation: string;
}

/** KYC presigned URLs — doc_pan_card replaces doc_experience (decided BN3) */
export interface KYCPresignedUrlSet {
  doc_aadhar:               PresignedUrlEntry;
  doc_pan_card:             PresignedUrlEntry;
  doc_medical_registration: PresignedUrlEntry;
  doc_medical_degree:       PresignedUrlEntry;
  s3_enabled:               boolean;
}

/** What GET /onboarding/documents returns for KYC status */
export interface KYCDocumentUrls {
  doc_aadhar_url:               string | null;
  doc_pan_card_url:             string | null;
  doc_medical_registration_url: string | null;
  doc_medical_degree_url:       string | null;
}

// ── Clinic Settings ──────────────────────────────────────────────────────────

export interface ClinicSettingsData {
  name:                  string;
  address:               string;
  phone:                 string;
  email:                 string;
  website:               string;
  timings_display:       string;
  logo_url?:             string | null;   // short-lived presigned GET for the clinic logo (read-only)
  letterhead_url?:       string | null;   // short-lived presigned GET for the prescription letterhead
  gstin?:                string | null;
  abdm_registration_id?: string | null;
  clinic_id?:            string;
}

/** One RBAC role assigned to a staff member (app.modules.rbac.user_roles). */
export interface StaffRoleSummary {
  role_id: string;
  name:    string;
}

/** A clinic staff member (any role) from GET /clinic/staff. */
export interface StaffMember {
  user_id:     string;
  first_name:  string;
  last_name:   string;
  email:       string;
  phone:       string | null;
  role:        string;
  designation: string | null;
  is_active:   boolean;
  /** RBAC roles assigned via the Roles & Permissions card — not yet the sole
   *  authorization source (see RBAC plan); `role` above stays authoritative
   *  until the backend enforce flip. */
  roles:       StaffRoleSummary[];
}

/** POST /clinic/staff body — add a clinic staff member. */
export interface StaffCreateInput {
  first_name: string;
  last_name:  string;
  email:      string;
  role:       "Doctor" | "Receptionist" | "Admin";
}

export interface AppointmentConfig {
  slot_duration_minutes: number;   // 10 | 15 | 20 | 30
  max_daily_bookings:    number;
  allow_online_booking:  boolean;
}

export interface HRFSettings {
  mandatory_vitals:    boolean;
  mandatory_diagnosis: boolean;
  lab_report_uploads:  boolean;
}

export interface ReschedulePolicySettings {
  self_reschedule_cutoff_hours:     number;
  max_self_reschedules_per_patient: number;
  excessive_churn_threshold:        number;
  excessive_churn_window_days:      number;
}

// ── Visit Notes edit policy ───────────────────────────────────────────────────

export interface VisitNoteEditPolicySettings {
  visit_note_edit_policy: "primary_only" | "any_with_access";
}

export interface ImmunizationSettings {
  iap_overlay_enabled: boolean;
}

// ── Access & Personalizations ────────────────────────────────────────────────

export interface PermissionRow {
  resource:     "patients" | "visits" | "prescriptions" | "billing" | "reports" | "settings";
  doctor:       boolean;
  receptionist: boolean;
  admin:        boolean;
}

// ── Finance & Billing ────────────────────────────────────────────────────────

export interface PaymentMethods {
  cash: boolean;
  upi:  boolean;
  card: boolean;
}

export interface FinanceSettings {
  gstin:                string;
  gst_rate:             0 | 5 | 12 | 18;
  invoice_prefix:       string;
  invoice_start_number: number;
  upi_id:               string;
  payment_methods:      PaymentMethods;
}

// ── Integrations ─────────────────────────────────────────────────────────────

export interface IntegrationStatus {
  service:    string;
  is_enabled: boolean;
}

// ── System & Preferences ─────────────────────────────────────────────────────

export interface NotificationChannel {
  sms:      boolean;
  whatsapp: boolean;
  push:     boolean;
}

/** Clinic-wide master switch per off-app channel. false = never routed.
 *  `email` persists but has no routing effect yet (no email notification channel). */
export interface ChannelMaster {
  email:    boolean;
  sms:      boolean;
  whatsapp: boolean;
}

export interface NotificationSettings {
  quiet_hours_start: string;                        // "HH:MM"
  quiet_hours_end:   string;                        // "HH:MM"
  channels:          Record<string, NotificationChannel>;
  channel_master:    ChannelMaster;
}

// ── Data & Privacy — full clinic export job ──────────────────────────────────

export type ClinicExportJobStatus = "pending" | "running" | "completed" | "failed" | "cancelled";

export interface ClinicExportJob {
  job_id:                  string;
  status:                  ClinicExportJobStatus;
  requested_at:            string;
  requested_by_name:       string | null;
  include_documents:       boolean;
  started_at:              string | null;
  completed_at:            string | null;
  row_count_manifest:      Record<string, number> | null;
  archive_size_bytes:      number | null;
  error_detail:            string | null;
  download_url:            string | null;
  download_url_expires_at: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// API
// Real calls  → endpoint exists today, uses apiRequest
// Stubs       → endpoint not built yet, returns Promise.resolve(mockData) + TODO
// ─────────────────────────────────────────────────────────────────────────────

export const SettingsAPI = {

  getCurrentUser(): Promise<CurrentUser> {
    return apiRequest<CurrentUser>(ROUTES.AUTH_ME);
  },

  // ── Profile & Account ──────────────────────────────────────────────────────

  /** Single call — backend GET /clinic/profile returns the aggregate. */
  getProfile(): Promise<ClinicProfile> {
    return apiRequest<ClinicProfile>(ROUTES.CLINIC_PROFILE);
  },

  /** Get-or-create the caller's own Doctor record so an admin-who-practises can
   *  submit their medical registration (NMC). The record is inactive until a
   *  PlatformAdmin verifies the registration. Returns the doctor_id. */
  enrollAsPractitioner(): Promise<{ doctor_id: string }> {
    return apiRequest<{ doctor_id: string }>("/me/practitioner-enrollment", {
      method: "POST",
    });
  },

  /** Read-only: the caller's linked doctor_id (or null) without creating a record
   *  or the verification gate that hides doctor_id in getProfile(). Used to load a
   *  submitted-but-unverified NMC on the compliance page. */
  getMyDoctorId(): Promise<{ doctor_id: string | null }> {
    return apiRequest<{ doctor_id: string | null }>("/me/practitioner-record");
  },

  saveProfile(data: ClinicProfileInput): Promise<ClinicProfile> {
    return apiRequest<ClinicProfile>(ROUTES.CLINIC_PROFILE, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  /** Upload a JPG/PNG profile photo (multipart). Returns the refreshed profile. */
  uploadProfilePhoto(file: File): Promise<ClinicProfile> {
    const fd = new FormData();
    fd.append("file", file);
    return apiRequest<ClinicProfile>(`${ROUTES.CLINIC_PROFILE}/photo`, {
      method: "PUT",
      body: fd,
    });
  },

  /** Remove the current profile photo. Returns the refreshed profile (photo_url null). */
  deleteProfilePhoto(): Promise<ClinicProfile> {
    return apiRequest<ClinicProfile>(`${ROUTES.CLINIC_PROFILE}/photo`, {
      method: "DELETE",
    });
  },

  getKYCDocuments(): Promise<KYCDocumentUrls> {
    return apiRequest<KYCDocumentUrls>(ROUTES.ONBOARDING_DOCS);
  },

  getKYCPresignedUrls(): Promise<KYCPresignedUrlSet> {
    return apiRequest<KYCPresignedUrlSet>(ROUTES.ONBOARDING_DOCS_PRESIGNED);
  },

  confirmKYCUpload(uploadedKeys: Record<string, string>): Promise<{ message: string }> {
    return apiRequest<{ message: string }>(ROUTES.ONBOARDING_DOCS, {
      method: "POST",
      body: JSON.stringify({ uploaded_keys: uploadedKeys }),
    });
  },

  // ── Clinic Settings ────────────────────────────────────────────────────────

  getClinicSettings(): Promise<ClinicSettingsData> {
    return apiRequest<ClinicSettingsData>(ROUTES.CLINIC_SETTINGS);
  },

  saveClinicSettings(data: Partial<ClinicSettingsData>): Promise<void> {
    return apiRequest<void>(ROUTES.CLINIC_SETTINGS, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  /** Upload a JPG/PNG clinic logo (multipart, Admin only). Returns the refreshed settings. */
  uploadClinicLogo(file: File): Promise<ClinicSettingsData> {
    const fd = new FormData();
    fd.append("file", file);
    return apiRequest<ClinicSettingsData>(`${ROUTES.CLINIC_SETTINGS}/logo`, {
      method: "PUT",
      body: fd,
    });
  },

  /** Remove the clinic logo (Admin only). Returns the refreshed settings (logo_url null). */
  deleteClinicLogo(): Promise<ClinicSettingsData> {
    return apiRequest<ClinicSettingsData>(`${ROUTES.CLINIC_SETTINGS}/logo`, {
      method: "DELETE",
    });
  },

  /** Upload a JPG/PNG letterhead banner (multipart, Admin only, ≤2 MB). Returns refreshed settings. */
  uploadClinicLetterhead(file: File): Promise<ClinicSettingsData> {
    const fd = new FormData();
    fd.append("file", file);
    return apiRequest<ClinicSettingsData>(ROUTES.CLINIC_LETTERHEAD, {
      method: "PUT",
      body: fd,
    });
  },

  /** Remove the clinic letterhead (Admin only). Returns refreshed settings (letterhead_url null). */
  deleteClinicLetterhead(): Promise<ClinicSettingsData> {
    return apiRequest<ClinicSettingsData>(ROUTES.CLINIC_LETTERHEAD, {
      method: "DELETE",
    });
  },

  /** Staff table — all clinic users (Doctor + Receptionist + Admin) from /clinic/staff. */
  getStaff(): Promise<StaffMember[]> {
    return apiRequest<StaffMember[]>(ROUTES.CLINIC_STAFF);
  },

  /** Add Staff modal — POST /clinic/staff (Admin only). Returns the new member. */
  addStaff(data: StaffCreateInput): Promise<StaffMember> {
    return apiRequest<StaffMember>(ROUTES.CLINIC_STAFF, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /** Revoke access for a staff member (Admin only). Disables account + forces sign-out. */
  revokeStaff(userId: string): Promise<StaffMember> {
    return apiRequest<StaffMember>(`/clinic/staff/${userId}/revoke`, {
      method: "POST",
    });
  },

  /** Re-enable a previously revoked staff member (Admin only). */
  reactivateStaff(userId: string): Promise<StaffMember> {
    return apiRequest<StaffMember>(`/clinic/staff/${userId}/reactivate`, {
      method: "POST",
    });
  },

  getAppointmentConfig(): Promise<AppointmentConfig> {
    return apiRequest<AppointmentConfig>(ROUTES.CLINIC_APPOINTMENT_CONFIG);
  },

  saveAppointmentConfig(data: AppointmentConfig): Promise<void> {
    return apiRequest<void>(ROUTES.CLINIC_APPOINTMENT_CONFIG, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  getReschedulePolicy(): Promise<ReschedulePolicySettings> {
    return apiRequest<ReschedulePolicySettings>(ROUTES.CLINIC_RESCHEDULE_POLICY);
  },

  /** Returns the policy as stored (the backend echoes it back). */
  saveReschedulePolicy(data: ReschedulePolicySettings): Promise<ReschedulePolicySettings> {
    return apiRequest<ReschedulePolicySettings>(ROUTES.CLINIC_RESCHEDULE_POLICY, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  getHRFSettings(): Promise<HRFSettings> {
    return apiRequest<HRFSettings>(ROUTES.CLINIC_HRF_SETTINGS);
  },

  saveHRFSettings(data: HRFSettings): Promise<void> {
    return apiRequest<void>(ROUTES.CLINIC_HRF_SETTINGS, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  getVisitNoteEditPolicy(): Promise<VisitNoteEditPolicySettings> {
    return apiRequest<VisitNoteEditPolicySettings>(ROUTES.CLINIC_VISIT_NOTE_EDIT_POLICY);
  },

  saveVisitNoteEditPolicy(data: VisitNoteEditPolicySettings): Promise<void> {
    return apiRequest<void>(ROUTES.CLINIC_VISIT_NOTE_EDIT_POLICY, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  getImmunizationSettings(): Promise<ImmunizationSettings> {
    return apiRequest<ImmunizationSettings>(ROUTES.CLINIC_IMMUNIZATION_SETTINGS);
  },

  saveImmunizationSettings(data: ImmunizationSettings): Promise<void> {
    return apiRequest<void>(ROUTES.CLINIC_IMMUNIZATION_SETTINGS, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  // ── Access & Personalizations ──────────────────────────────────────────────

  getPermissions(): Promise<PermissionRow[]> {
    return apiRequest<PermissionRow[]>(ROUTES.CLINIC_PERMISSIONS);
  },

  savePermissions(data: PermissionRow[]): Promise<void> {
    return apiRequest<void>(ROUTES.CLINIC_PERMISSIONS, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  // ── Finance & Billing ──────────────────────────────────────────────────────

  getFinanceSettings(): Promise<FinanceSettings> {
    return apiRequest<FinanceSettings>(ROUTES.CLINIC_FINANCE_SETTINGS);
  },

  saveFinanceSettings(data: FinanceSettings): Promise<void> {
    return apiRequest<void>(ROUTES.CLINIC_FINANCE_SETTINGS, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  // ── Integrations ──────────────────────────────────────────────────────────

  getIntegrations(): Promise<IntegrationStatus[]> {
    // razorpay is still hardcoded false on the backend until Suyush's flag PR merges.
    return apiRequest<IntegrationStatus[]>(ROUTES.CLINIC_INTEGRATIONS);
  },

  // ── System & Preferences ──────────────────────────────────────────────────

  getNotificationSettings(): Promise<NotificationSettings> {
    return apiRequest<NotificationSettings>(ROUTES.CLINIC_NOTIFICATION_SETTINGS);
  },

  saveNotificationSettings(data: NotificationSettings): Promise<void> {
    return apiRequest<void>(ROUTES.CLINIC_NOTIFICATION_SETTINGS, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  /** Authorize + audit a full-clinic PHI backup export (Admin only). Must resolve
   *  before the client builds the offline Excel backup: the file is generated
   *  client-side and never reaches the backend, so this call is the only
   *  server-side record of who exported the clinic's PHI, when. Rejects with 403
   *  for non-admins; callers fail closed (no file) on any error. */
  recordBackupExport(windowDays: number): Promise<{ authorized: boolean }> {
    return apiRequest<{ authorized: boolean }>(ROUTES.CLINIC_BACKUP_EXPORT, {
      method: "POST",
      body: JSON.stringify({ window_days: windowDays }),
    });
  },

  // ── Data & Privacy — full clinic export job ──────────────────────────────

  requestClinicExport(includeDocuments: boolean): Promise<ClinicExportJob> {
    return apiRequest<ClinicExportJob>(ROUTES.CLINIC_EXPORT, {
      method: "POST",
      body: JSON.stringify({ include_documents: includeDocuments }),
    });
  },

  listClinicExports(): Promise<ClinicExportJob[]> {
    return apiRequest<ClinicExportJob[]>(ROUTES.CLINIC_EXPORT);
  },

  getClinicExportStatus(jobId: string): Promise<ClinicExportJob> {
    return apiRequest<ClinicExportJob>(ROUTES.CLINIC_EXPORT_JOB(jobId));
  },

  cancelClinicExport(jobId: string): Promise<ClinicExportJob> {
    return apiRequest<ClinicExportJob>(ROUTES.CLINIC_EXPORT_JOB_CANCEL(jobId), {
      method: "POST",
    });
  },
};
